// Shared portal-user resolution for the brief recipient lifecycle. All brief
// comms (initial send, resend, reminder, 24h cron, amend update, award win,
// award decline, brief closed) must target the supplier's portal users —
// suppliers.main_contact_email is reserved for the new-supplier setup-form
// invitation only.
//
// brief_recipients.selected_contacts stays as the send-time snapshot. New
// entries are written with type: 'portal_user' (carrying the supplier_user.id
// for traceability); older 'primary' / 'secondary' rows still on existing
// recipients are treated as LEGACY and surface a "Sync recipients to current
// portal users" action on the brief detail.

import { supabase } from '../supabaseClient';

// Load active supplier_users for the given supplier ids and return a Map
// keyed by supplier_id → array of { id, full_name, email }. Disabled portal
// users (is_active=false) are excluded; suppliers with zero active users get
// an empty array (callers use that to block sends).
export async function fetchActivePortalUsersBySupplier(supplierIds) {
  const ids = Array.isArray(supplierIds) ? Array.from(new Set(supplierIds.filter(Boolean))) : [];
  const out = new Map();
  if (ids.length === 0) return out;
  const { data, error } = await supabase
    .from('supplier_users')
    .select('id, supplier_id, full_name, email, is_active')
    .in('supplier_id', ids)
    .eq('is_active', true)
    .order('full_name', { ascending: true });
  if (error) return out;
  for (const u of data || []) {
    if (!u.email) continue;
    if (!out.has(u.supplier_id)) out.set(u.supplier_id, []);
    out.get(u.supplier_id).push({ id: u.id, full_name: u.full_name || '', email: u.email });
  }
  return out;
}

// Map portal users → selected_contacts rows ready for brief_recipients. All
// users default to selected=true; callers (AddBrief tile, Add Supplier flow)
// allow the operator to uncheck individuals before save.
export function buildSelectedContactsFromPortalUsers(portalUsers, { selectedByDefault = true } = {}) {
  return (portalUsers || []).map(u => ({
    name: u.full_name || '',
    email: u.email,
    user_id: u.id || null,
    type: 'portal_user',
    selected: selectedByDefault,
  }));
}

// A recipient is "legacy" if its snapshot pre-dates the portal-user routing
// migration — selected_contacts is empty (very old rows that fell through to
// a main_contact fallback at send-time) or every entry carries the old
// 'primary' / 'secondary' type tag. Pure read; no DB call.
export function isLegacyRecipient(recipient) {
  const contacts = Array.isArray(recipient?.selected_contacts) ? recipient.selected_contacts : [];
  if (contacts.length === 0) return true;
  return contacts.every(c => c?.type === 'primary' || c?.type === 'secondary');
}

// Friendly summary of who'll receive the email for a single recipient row,
// drawn from the snapshot. Returns { count, primary, all } so the caller can
// render "N. Phillips · n.phillips@…  +2 more" without re-deriving anywhere.
export function contactsSummary(recipient) {
  const contacts = Array.isArray(recipient?.selected_contacts) ? recipient.selected_contacts : [];
  const active = contacts.filter(c => c?.selected && c?.email);
  return {
    count: active.length,
    primary: active[0] || null,
    all: active,
  };
}
